import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { Task, SavedTask, SubTask, CompletionLog, RepeatPattern, TaskPriority } from '@/lib/types'
import {
  getAllTasks,
  addTask as dbAddTask,
  updateTask as dbUpdateTask,
  deleteTask as dbDeleteTask,
  addCompletionLog,
  deleteCompletionLogBySyncId as dbDeleteCompletionLogBySyncId,
  findCompletionLogForUncomplete as dbFindCompletionLogForUncomplete,
  bulkUpdateTasks as dbBulkUpdateTasks,
  bulkDeleteTasks as dbBulkDeleteTasks,
  bulkCompleteTasks as dbBulkCompleteTasks,
  db,
} from '@/services/database'
import { useUndoStore } from './undoStore'
import { useActivityStore } from './activityStore'
import { useProfileStore } from './profileStore'
import { pushTask, debouncedPushTask, pushCompletionLog, deleteCompletionLogFromCloud, deleteTaskFromCloud, addToSyncQueueExternal } from '@/services/firestoreSync'
import { generateSyncId } from '@/services/database'
import { pushTaskToGoogle, debouncedPushGoogleTask, deleteTaskFromGoogle } from '@/services/googleTasksSync'
import { getToken } from '@/services/googleDrive'
import { useSettingsStore } from '@/stores/settingsStore'

/** Check if Google Tasks sync is enabled and authenticated */
function isGoogleTasksSyncEnabled(): boolean {
  return useSettingsStore.getState().settings.googleCalendarEnabled && !!getToken()
}

/** #2: 클라우드 push 실패 시 사용자에게 알림 + H1: sync queue에 추가 */
function handleCloudPushError(err: unknown, context: string, syncId?: string, type: 'push-task' | 'delete-task' = 'push-task'): void {
  console.error(`[CloudSync] ${context}:`, err)
  // H1: sync queue에 추가하여 나중에 재시도
  if (syncId) {
    addToSyncQueueExternal({ type, syncId, timestamp: new Date().toISOString() })
  }
  import('@/stores/toastStore').then(({ useToastStore }) => {
    useToastStore.getState().showToast('클라우드 동기화에 실패했습니다. 나중에 다시 시도됩니다.', 'warning')
  }).catch(() => {})
}

import { formatDate as toLocalDateStr } from '@/utils/format'
import { getNextOccurrence } from '@/lib/repeatUtils'

// ─── Repeat Task Generation ──────────────────────
// Delegate the recurrence rule to repeatUtils.getNextOccurrence so the two
// never diverge. It anchors monthly/yearly to pattern.monthDay (no permanent
// clamp drift to the 28th), honors weekly weekDays, and keeps all date math
// local (parseISO + addX + format) to avoid UTC off-by-one. Returns null when
// the pattern is exhausted; we normalize to undefined for this store's shape.
function generateNextDueDate(dueDate: string, repeat: RepeatPattern): string | undefined {
  return getNextOccurrence(dueDate, repeat) ?? undefined
}

function generateNextTask(task: Task): Omit<Task, 'id'> | null {
  if (task.repeat.type === 'none' || !task.dueDate) return null

  const nextDueDate = generateNextDueDate(task.dueDate, task.repeat)
  if (!nextDueDate) return null

  const now = new Date().toISOString()

  // #5: monthly/yearly 반복인데 monthDay가 없으면(레거시/UI 미저장) 현재 task의 dueDate
  // 일(day-of-month)을 spawn된 task의 repeat에 고정 — 다음 생성부터 28일로 영구 drift하는
  // 문제를 차단 (예: 31일 → 28일 → 31일 복구).
  const nextRepeat: RepeatPattern = { ...task.repeat }
  if (
    (nextRepeat.type === 'monthly' || nextRepeat.type === 'yearly') &&
    nextRepeat.monthDay == null
  ) {
    const anchorDay = Number(task.dueDate.slice(8, 10))
    if (Number.isFinite(anchorDay) && anchorDay >= 1 && anchorDay <= 31) {
      nextRepeat.monthDay = anchorDay
    }
  }

  return {
    title: task.title,
    categoryId: task.categoryId,
    status: 'pending',
    priority: task.priority,
    isFlagged: false,
    isStarred: false,
    dueDate: nextDueDate,
    dueTime: task.dueTime,
    alarm: task.alarm ? { ...task.alarm } : { enabled: false },
    repeat: nextRepeat,
    memo: task.memo,
    subtasks: task.subtasks.map((st) => ({
      ...st,
      isCompleted: false,
    })),
    completedAt: undefined,
    createdAt: now,
    updatedAt: now,
    sortOrder: task.sortOrder,
  }
}

// ─── Store ────────────────────────────────────────
interface TaskState {
  tasks: SavedTask[]
  activeTaskId: number | null
  isLoading: boolean
  /** initialize/refreshFromDb 완료 후 true. 로딩 스켈레톤 ↔ 빈 상태 분기에 사용 */
  isHydrated: boolean
  error: string | null

  // Task actions
  initialize: () => Promise<void>
  addTask: (data: Omit<Task, 'id'>) => Promise<number | undefined>
  updateTask: (id: number, updates: Partial<Task>) => Promise<void>
  deleteTask: (id: number) => Promise<void>
  toggleComplete: (id: number) => Promise<void>
  toggleFlag: (id: number) => Promise<void>
  toggleStar: (id: number) => Promise<void>
  setActiveTask: (id: number | null) => void
  clearError: () => void

  // Subtask actions
  addSubtask: (taskId: number, title: string) => Promise<void>
  updateSubtask: (taskId: number, subtaskId: string, updates: Partial<SubTask>) => Promise<void>
  deleteSubtask: (taskId: number, subtaskId: string) => Promise<void>
  toggleSubtaskComplete: (taskId: number, subtaskId: string) => Promise<void>

  // Reorder actions
  reorderTasks: (activeId: number, overId: number) => Promise<void>
  reorderSubtasks: (taskId: number, activeId: string, overId: string) => Promise<void>

  // Batch actions
  batchComplete: (ids: number[]) => Promise<void>
  batchDelete: (ids: number[]) => Promise<void>
  batchMoveCategory: (ids: number[], categoryId: number | null) => Promise<void>
  batchSetPriority: (ids: number[], priority: TaskPriority) => Promise<void>
  refreshFromDb: () => Promise<void>
  seedTutorialTasks: () => Promise<void>
}

export const useTaskStore = create<TaskState>()(
  devtools(
    (set, get) => {
      // ─── Shared completion helpers (single + batch must stay symmetric) ───

      /**
       * #1/#2: 완료 로그 1건을 생성·저장·클라우드 push. 생성된 logSyncId를 반환하여
       * undo 시 동일 로그를 삭제할 수 있도록 한다. taskSyncId가 없으면 cloud push는 생략.
       */
      const createCompletionLog = async (taskId: number, completedAt: string, date: string, taskSyncId?: string): Promise<string> => {
        const logSyncId = generateSyncId()
        const log: Omit<CompletionLog, 'id'> = { syncId: logSyncId, taskId, completedAt, date }
        await addCompletionLog(log)
        if (taskSyncId) {
          pushCompletionLog(log as CompletionLog, taskSyncId).catch(console.error)
        }
        return logSyncId
      }

      /** #1/#2: 완료 로그를 로컬 + 클라우드에서 삭제 (완료 취소/undo 시 orphan 방지). */
      const removeCompletionLog = async (logSyncId: string): Promise<void> => {
        await dbDeleteCompletionLogBySyncId(logSyncId)
        deleteCompletionLogFromCloud(logSyncId).catch(console.error)
      }

      /**
       * #1: 작업을 직접 완료 취소할 때, 그 완료에 대응하는 로그를 찾아 로컬+클라우드에서 제거.
       * completedAt이 일치하는 로그를 우선, 없으면 가장 최근 완료 로그를 폴백으로 삭제한다.
       * 제거한 로그의 syncId를 반환(없으면 undefined) — redo 시 동일 로그를 재생성하기 위함.
       */
      const removeCompletionLogForTask = async (taskId: number, completedAt?: string): Promise<string | undefined> => {
        const existing = await dbFindCompletionLogForUncomplete(taskId, completedAt)
        if (existing?.syncId) {
          await removeCompletionLog(existing.syncId)
          return existing.syncId
        }
        return undefined
      }

      /**
       * #10/#20: 반복 작업의 다음 회차를 생성·저장·동기화하고 생성된 SavedTask를 반환.
       * 반복이 아니거나 생성 불가 시 null. toggleComplete/batchComplete가 공유하여
       * 두 경로가 절대 어긋나지 않도록 한다.
       */
      const spawnNextOccurrence = async (task: Task): Promise<SavedTask | null> => {
        if (task.repeat.type === 'none') return null
        const nextTaskData = generateNextTask(task)
        if (!nextTaskData) return null
        const nextSyncId = generateSyncId()
        const nextDataWithSync = { ...nextTaskData, syncId: nextSyncId }
        const nextId = await dbAddTask(nextDataWithSync)
        const nextTask: SavedTask = { ...nextDataWithSync, id: nextId }
        set((state) => ({ tasks: [...state.tasks, nextTask] }))
        pushTask(nextTask, true).catch(console.error)
        if (isGoogleTasksSyncEnabled()) pushTaskToGoogle(nextTask).catch(console.error)
        return nextTask
      }

      /**
       * #20: spawn된 다음 회차 작업을 로컬 + 상태 + 클라우드(+Google)에서 제거 (완료 undo 시).
       */
      const removeSpawnedTask = async (spawned: SavedTask): Promise<void> => {
        // syncId로 현재 행을 재조회 — undo 시점에 id가 바뀌었을 수 있음
        const current = get().tasks.find((t) => t.syncId === spawned.syncId)
        const targetId = current?.id ?? spawned.id
        await dbDeleteTask(targetId)
        set((state) => ({ tasks: state.tasks.filter((t) => t.id !== targetId) }))
        deleteTaskFromCloud(spawned.syncId).catch(console.error)
        const googleTaskId = current?.googleTaskId ?? spawned.googleTaskId
        if (googleTaskId && isGoogleTasksSyncEnabled()) {
          deleteTaskFromGoogle(googleTaskId).catch(console.error)
        }
      }

      return {
      tasks: [],
      activeTaskId: null,
      isLoading: false,
      isHydrated: false,
      error: null,

      initialize: async () => {
        set({ isLoading: true, error: null })
        try {
          const tasks = await getAllTasks() as SavedTask[]
          set({ tasks, isLoading: false, isHydrated: true })
        } catch (err) {
          // 실패해도 무한 스켈레톤 대신 빈/에러 상태를 보이도록 hydrated 처리
          set({ error: (err as Error).message, isLoading: false, isHydrated: true })
        }
      },

      addTask: async (data) => {
        try {
          const syncId = data.syncId || generateSyncId()
          const taskData = { ...data, syncId }
          const id = await dbAddTask(taskData)
          const newTask: SavedTask = { ...taskData, id }
          set((state) => ({ tasks: [...state.tasks, newTask] }))

          useActivityStore.getState().addLog(id, data.title, 'task-created')
          pushTask(newTask, true).catch(err => handleCloudPushError(err, 'addTask', newTask.syncId))
          if (isGoogleTasksSyncEnabled()) pushTaskToGoogle(newTask).catch(console.error)
          return id
        } catch (err) {
          set({ error: (err as Error).message })
          return undefined
        }
      },

      updateTask: async (id, updates) => {
        try {
          // Track reschedule count when dueDate is pushed forward
          if (updates.dueDate) {
            const existing = get().tasks.find(t => t.id === id)
            if (existing?.dueDate && updates.dueDate > existing.dueDate) {
              updates = { ...updates, rescheduleCount: (existing.rescheduleCount || 0) + 1 }
            }
          }
          await dbUpdateTask(id, updates)
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
            ),
          }))
          const updated = get().tasks.find(t => t.id === id)
          if (updated) {
            pushTask(updated).catch(err => handleCloudPushError(err, 'updateTask', updated.syncId))
            if (isGoogleTasksSyncEnabled()) debouncedPushGoogleTask(updated)
          }
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      deleteTask: async (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return

        // Undo 스냅샷 저장
        const snapshot = { ...task }
        useUndoStore.getState().pushAction({
          type: 'delete',
          label: '작업이 삭제되었습니다',
          undo: async () => {
            const newId = await dbAddTask({ ...snapshot, id: undefined } as Omit<Task, 'id'>)
            const restored: SavedTask = { ...snapshot, id: newId }
            set((state) => ({ tasks: [...state.tasks, restored] }))
            // C2: 클라우드에도 복원 동기화
            pushTask(restored).catch(err => handleCloudPushError(err, 'undoDelete', restored.syncId))
          },
          redo: async () => {
            const current = get().tasks.find((t) => t.syncId === snapshot.syncId)
            if (!current) return
            await dbDeleteTask(current.id)
            set((state) => ({
              tasks: state.tasks.filter((t) => t.id !== current.id),
              activeTaskId: state.activeTaskId === current.id ? null : state.activeTaskId,
            }))
            deleteTaskFromCloud(snapshot.syncId).catch(err =>
              handleCloudPushError(err, 'redoDelete', snapshot.syncId, 'delete-task')
            )
          },
        })

        // Optimistic update — UI first
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id),
          activeTaskId: state.activeTaskId === id ? null : state.activeTaskId,
        }))

        useActivityStore.getState().addLog(id, task.title, 'task-deleted')

        try {
          await dbDeleteTask(id)
          deleteTaskFromCloud(task.syncId).catch(err => handleCloudPushError(err, 'deleteTask', task.syncId, 'delete-task'))
          if (task.googleTaskId && isGoogleTasksSyncEnabled()) {
            deleteTaskFromGoogle(task.googleTaskId).catch(console.error)
          }
        } catch (err) {
          // Rollback on failure — re-add the task
          set((state) => ({
            tasks: [...state.tasks, snapshot],
            error: (err as Error).message,
          }))
        }
      },

      toggleComplete: async (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return

        const wasCompleted = task.status === 'completed'

        // #1/#20: 완료 로그 syncId와 spawn된 다음 회차 작업을 클로저에 캡처하여
        // undo/redo가 완료↔미완료를 데이터 측면까지 완전히 대칭으로 되돌릴 수 있게 한다.
        // pushAction이 클로저를 먼저 등록하므로 변수는 미리 선언하고 try 블록에서 할당.
        let createdLogSyncId: string | undefined
        let generatedNext: SavedTask | null = null
        // 완료 취소(uncomplete) 시 제거한 로그의 syncId — redo에서 동일 로그 재생성용
        let removedLogSyncId: string | undefined

        // Undo 스냅샷
        useUndoStore.getState().pushAction({
          type: 'complete',
          label: wasCompleted ? '작업 완료가 취소되었습니다' : '작업이 완료되었습니다',
          undo: async () => {
            if (wasCompleted) {
              // 미완료 → (원래) 완료로 복원: 직접 uncomplete에서 제거했던 로그를 재생성
              await dbUpdateTask(id, { status: 'completed', completedAt: task.completedAt })
              set((state) => ({
                tasks: state.tasks.map((t) =>
                  t.id === id ? { ...t, status: 'completed' as const, completedAt: task.completedAt } : t
                ),
              }))
              const restoredAt = task.completedAt ?? new Date().toISOString()
              removedLogSyncId = await createCompletionLog(id, restoredAt, toLocalDateStr(new Date(restoredAt)), task.syncId)
            } else {
              // 완료 → 미완료로 되돌림: 생성된 완료 로그와 spawn된 다음 회차를 함께 제거
              await dbUpdateTask(id, { status: 'pending', completedAt: undefined })
              set((state) => ({
                tasks: state.tasks.map((t) =>
                  t.id === id ? { ...t, status: 'pending' as const, completedAt: undefined } : t
                ),
              }))
              if (createdLogSyncId) await removeCompletionLog(createdLogSyncId)
              if (generatedNext) { await removeSpawnedTask(generatedNext); generatedNext = null }
            }
            useProfileStore.getState().calculateStreak()
          },
          redo: async () => {
            if (wasCompleted) {
              // 다시 완료 취소: 재생성했던 로그를 제거
              await dbUpdateTask(id, { status: 'pending', completedAt: undefined })
              set((state) => ({
                tasks: state.tasks.map((t) =>
                  t.id === id ? { ...t, status: 'pending' as const, completedAt: undefined } : t
                ),
              }))
              if (removedLogSyncId) { await removeCompletionLog(removedLogSyncId); removedLogSyncId = undefined }
            } else {
              // 다시 완료 처리: 완료 로그와 다음 회차를 재생성
              const now = new Date().toISOString()
              await dbUpdateTask(id, { status: 'completed', completedAt: now })
              set((state) => ({
                tasks: state.tasks.map((t) =>
                  t.id === id ? { ...t, status: 'completed' as const, completedAt: now, updatedAt: now } : t
                ),
              }))
              createdLogSyncId = await createCompletionLog(id, now, toLocalDateStr(new Date()), task.syncId)
              generatedNext = await spawnNextOccurrence(task)
            }
            useProfileStore.getState().calculateStreak()
          },
        })

        if (task.status === 'pending') {
          const now = new Date().toISOString()
          const todayDate = toLocalDateStr(new Date())

          // Optimistic update — UI first
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, status: 'completed' as const, completedAt: now, updatedAt: now } : t
            ),
          }))

          useActivityStore.getState().addLog(id, task.title, 'task-completed')
          useProfileStore.getState().calculateStreak()

          try {
            await dbUpdateTask(id, { status: 'completed', completedAt: now })

            createdLogSyncId = await createCompletionLog(id, now, todayDate, task.syncId)

            const completedTask = get().tasks.find(t => t.id === id)
            if (completedTask) {
              pushTask(completedTask).catch(console.error)
              if (isGoogleTasksSyncEnabled()) pushTaskToGoogle(completedTask).catch(console.error)
            }

            // Generate next repeat task if applicable (#10/#20: shared helper)
            generatedNext = await spawnNextOccurrence(task)
          } catch (err) {
            // Rollback on failure
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id === id ? { ...t, status: 'pending' as const, completedAt: undefined, updatedAt: task.updatedAt } : t
              ),
              error: (err as Error).message,
            }))
          }
        } else {
          const updatedAt = new Date().toISOString()

          // Optimistic update — UI first
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, status: 'pending' as const, completedAt: undefined, updatedAt } : t
            ),
          }))

          useActivityStore.getState().addLog(id, task.title, 'task-uncompleted')

          try {
            await dbUpdateTask(id, { status: 'pending', completedAt: undefined })
            // #1: 직접 완료 취소 시 이 완료에 대응하는 로그를 로컬+클라우드에서 제거하여
            // orphan 로그가 streak/통계를 부풀리지 않게 한다.
            await removeCompletionLogForTask(id, task.completedAt)
            useProfileStore.getState().calculateStreak()
            const uncompletedTask = get().tasks.find(t => t.id === id)
            if (uncompletedTask) {
              pushTask(uncompletedTask).catch(console.error)
              if (isGoogleTasksSyncEnabled()) pushTaskToGoogle(uncompletedTask).catch(console.error)
            }
          } catch (err) {
            // Rollback on failure
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id === id ? { ...t, status: 'completed' as const, completedAt: task.completedAt, updatedAt: task.updatedAt } : t
              ),
              error: (err as Error).message,
            }))
          }
        }
      },

      toggleFlag: async (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return

        const isFlagged = !task.isFlagged
        const updatedAt = new Date().toISOString()

        // Optimistic update — UI first
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, isFlagged, updatedAt } : t
          ),
        }))

        useActivityStore.getState().addLog(id, task.title, isFlagged ? 'task-flagged' : 'task-unflagged')

        try {
          await dbUpdateTask(id, { isFlagged })
          const flaggedTask = get().tasks.find(t => t.id === id)
          if (flaggedTask) debouncedPushTask(flaggedTask)
        } catch (err) {
          // Rollback on failure
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, isFlagged: task.isFlagged, updatedAt: task.updatedAt } : t
            ),
            error: (err as Error).message,
          }))
        }
      },

      toggleStar: async (id) => {
        const task = get().tasks.find((t) => t.id === id)
        if (!task) return

        try {
          const isStarred = !task.isStarred
          await dbUpdateTask(id, { isStarred })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === id ? { ...t, isStarred, updatedAt: new Date().toISOString() } : t
            ),
          }))
          const starredTask = get().tasks.find(t => t.id === id)
          if (starredTask) debouncedPushTask(starredTask)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      setActiveTask: (id) => {
        set({ activeTaskId: id })
      },

      clearError: () => {
        set({ error: null })
      },

      // ─── Subtask Actions ──────────────────────────
      addSubtask: async (taskId, title) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return

        const newSubtask: SubTask = {
          id: crypto.randomUUID(),
          title,
          isCompleted: false,
          sortOrder: task.subtasks.length,
          createdAt: new Date().toISOString(),
        }

        const updatedSubtasks = [...task.subtasks, newSubtask]

        try {
          await dbUpdateTask(taskId, { subtasks: updatedSubtasks })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId
                ? { ...t, subtasks: updatedSubtasks, updatedAt: new Date().toISOString() }
                : t
            ),
          }))

          useActivityStore.getState().addLog(taskId, task.title, 'subtask-added', title)
          const parentAfterAdd = get().tasks.find(t => t.id === taskId)
          if (parentAfterAdd) pushTask(parentAfterAdd).catch(console.error)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      updateSubtask: async (taskId, subtaskId, updates) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return

        const updatedSubtasks = task.subtasks.map((st) =>
          st.id === subtaskId ? { ...st, ...updates } : st
        )

        try {
          await dbUpdateTask(taskId, { subtasks: updatedSubtasks })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId
                ? { ...t, subtasks: updatedSubtasks, updatedAt: new Date().toISOString() }
                : t
            ),
          }))
          const updParent = get().tasks.find(t => t.id === taskId)
          if (updParent) pushTask(updParent).catch(console.error)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      deleteSubtask: async (taskId, subtaskId) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return

        const updatedSubtasks = task.subtasks.filter((st) => st.id !== subtaskId)

        try {
          await dbUpdateTask(taskId, { subtasks: updatedSubtasks })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId
                ? { ...t, subtasks: updatedSubtasks, updatedAt: new Date().toISOString() }
                : t
            ),
          }))
          const delParent = get().tasks.find(t => t.id === taskId)
          if (delParent) pushTask(delParent).catch(console.error)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      toggleSubtaskComplete: async (taskId, subtaskId) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return

        const updatedSubtasks = task.subtasks.map((st) =>
          st.id === subtaskId
            ? {
                ...st,
                isCompleted: !st.isCompleted,
                completedAt: !st.isCompleted ? new Date().toISOString() : undefined,
              }
            : st
        )

        try {
          await dbUpdateTask(taskId, { subtasks: updatedSubtasks })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId
                ? { ...t, subtasks: updatedSubtasks, updatedAt: new Date().toISOString() }
                : t
            ),
          }))
          const togParent = get().tasks.find(t => t.id === taskId)
          if (togParent) pushTask(togParent).catch(console.error)

          // 전체 하위 작업 완료 시 상위 작업 자동 완료 (toggleComplete가 자체 Undo 제공)
          const allDone = updatedSubtasks.length > 0 && updatedSubtasks.every(st => st.isCompleted)
          if (allDone && task.status === 'pending') {
            await get().toggleComplete(taskId)
          }
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      // ─── Reorder Actions ──────────────────────────
      reorderTasks: async (activeId, overId) => {
        const { tasks } = get()
        const sortedPending = [...tasks]
          .filter((t) => t.status === 'pending')
          .sort((a, b) => a.sortOrder - b.sortOrder)

        const oldIndex = sortedPending.findIndex((t) => t.id === activeId)
        const newIndex = sortedPending.findIndex((t) => t.id === overId)
        if (oldIndex === -1 || newIndex === -1) return

        const reordered = [...sortedPending]
        const [moved] = reordered.splice(oldIndex, 1)
        reordered.splice(newIndex, 0, moved)

        // sortOrder 갱신
        const updates: { id: number; sortOrder: number }[] = reordered.map((t, i) => ({
          id: t.id,
          sortOrder: i,
        }))

        try {
          await db.transaction('rw', db.tasks, async () => {
            for (const u of updates) {
              await db.tasks.update(u.id, { sortOrder: u.sortOrder, updatedAt: new Date().toISOString() })
            }
          })

          set((state) => ({
            tasks: state.tasks.map((t) => {
              const update = updates.find((u) => u.id === t.id)
              return update ? { ...t, sortOrder: update.sortOrder } : t
            }),
          }))
          for (const u of updates) {
            const t = get().tasks.find(task => task.id === u.id)
            if (t) pushTask(t).catch(console.error)
          }
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      reorderSubtasks: async (taskId, activeId, overId) => {
        const task = get().tasks.find((t) => t.id === taskId)
        if (!task) return

        const subtasks = [...task.subtasks].sort((a, b) => a.sortOrder - b.sortOrder)
        const oldIndex = subtasks.findIndex((s) => s.id === activeId)
        const newIndex = subtasks.findIndex((s) => s.id === overId)
        if (oldIndex === -1 || newIndex === -1) return

        const [moved] = subtasks.splice(oldIndex, 1)
        subtasks.splice(newIndex, 0, moved)

        const updatedSubtasks = subtasks.map((st, i) => ({ ...st, sortOrder: i }))

        try {
          await dbUpdateTask(taskId, { subtasks: updatedSubtasks })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              t.id === taskId ? { ...t, subtasks: updatedSubtasks, updatedAt: new Date().toISOString() } : t
            ),
          }))
          const reorderedParent = get().tasks.find(t => t.id === taskId)
          if (reorderedParent) pushTask(reorderedParent).catch(console.error)
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      // ─── Batch Actions ────────────────────────────
      batchComplete: async (ids) => {
        const tasksToComplete = get().tasks.filter((t) => ids.includes(t.id) && t.status === 'pending')
        if (tasksToComplete.length === 0) return

        const completedIds = tasksToComplete.map((t) => t.id)

        // Undo 스냅샷
        const snapshots = tasksToComplete.map((t) => ({ ...t }))
        // #2/#10: 이 배치에서 생성한 완료 로그 syncId와 spawn된 다음 회차 작업을 캡처하여
        // undo가 orphan 로그/중복 회차를 남기지 않고 완전히 되돌리고, redo가 동일 상태로 복원.
        let createdLogSyncIds: string[] = []
        let spawnedTasks: SavedTask[] = []

        useUndoStore.getState().pushAction({
          type: 'batch-complete',
          label: `${tasksToComplete.length}개 작업이 완료되었습니다`,
          undo: async () => {
            for (const snap of snapshots) {
              await dbUpdateTask(snap.id, { status: 'pending', completedAt: undefined })
            }
            set((state) => ({
              tasks: state.tasks.map((t) => {
                const snap = snapshots.find((s) => s.id === t.id)
                return snap ? { ...t, status: 'pending' as const, completedAt: undefined } : t
              }),
            }))
            // 생성한 완료 로그 제거 (로컬 + 클라우드)
            for (const logSyncId of createdLogSyncIds) await removeCompletionLog(logSyncId)
            createdLogSyncIds = []
            // spawn된 다음 회차 제거 (로컬 + 상태 + 클라우드 + Google)
            for (const spawned of spawnedTasks) await removeSpawnedTask(spawned)
            spawnedTasks = []
            useProfileStore.getState().calculateStreak()
          },
          redo: async () => {
            const now = new Date().toISOString()
            const todayDate = toLocalDateStr(new Date())
            // 완료 로그 재생성 후 단일 트랜잭션으로 상태 갱신 + 로그 추가
            const newLogSyncIds: string[] = []
            const logs: Omit<CompletionLog, 'id'>[] = completedIds.map((tid) => {
              const logSyncId = generateSyncId()
              newLogSyncIds.push(logSyncId)
              return { syncId: logSyncId, taskId: tid, completedAt: now, date: todayDate }
            })
            await dbBulkCompleteTasks(completedIds, now, logs)
            createdLogSyncIds = newLogSyncIds
            set((state) => ({
              tasks: state.tasks.map((t) =>
                completedIds.includes(t.id) ? { ...t, status: 'completed' as const, completedAt: now, updatedAt: now } : t
              ),
            }))
            // 완료 task + 완료 로그 cloud push
            for (const task of tasksToComplete) {
              const t = get().tasks.find((x) => x.id === task.id)
              if (t) pushTask(t).catch(console.error)
              const logSyncId = logs.find((l) => l.taskId === task.id)?.syncId
              if (logSyncId && task.syncId) {
                pushCompletionLog({ syncId: logSyncId, taskId: task.id, completedAt: now, date: todayDate } as CompletionLog, task.syncId).catch(console.error)
              }
            }
            // 반복 작업 다음 회차 재생성
            spawnedTasks = []
            for (const task of tasksToComplete) {
              try {
                const spawned = await spawnNextOccurrence(task)
                if (spawned) spawnedTasks.push(spawned)
              } catch (e) { console.error('[batchComplete] redo spawn 실패:', e) }
            }
            useProfileStore.getState().calculateStreak()
          },
        })

        try {
          const now = new Date().toISOString()
          const todayDate = toLocalDateStr(new Date())

          // #37: 작업 상태 갱신 + 완료 로그 추가를 단일 트랜잭션으로 (부분 실패 방지)
          const logs: Omit<CompletionLog, 'id'>[] = tasksToComplete.map((task) => {
            const logSyncId = generateSyncId()
            createdLogSyncIds.push(logSyncId)
            return { syncId: logSyncId, taskId: task.id, completedAt: now, date: todayDate }
          })
          await dbBulkCompleteTasks(completedIds, now, logs)

          // 완료 로그 cloud push
          for (const task of tasksToComplete) {
            if (task.syncId) {
              const logSyncId = logs.find((l) => l.taskId === task.id)?.syncId
              if (logSyncId) {
                pushCompletionLog({ syncId: logSyncId, taskId: task.id, completedAt: now, date: todayDate } as CompletionLog, task.syncId).catch(console.error)
              }
            }
          }

          set((state) => ({
            tasks: state.tasks.map((t) =>
              completedIds.includes(t.id) ? { ...t, status: 'completed' as const, completedAt: now, updatedAt: now } : t
            ),
          }))
          for (const id of completedIds) {
            const t = get().tasks.find(task => task.id === id)
            if (t) pushTask(t).catch(console.error)
          }

          // #10: 반복 작업의 다음 회차 생성 (toggleComplete와 동일한 공유 헬퍼 사용)
          for (const task of tasksToComplete) {
            try {
              const spawned = await spawnNextOccurrence(task)
              if (spawned) spawnedTasks.push(spawned)
            } catch (e) { console.error('[batchComplete] spawn 실패:', e) }
          }

          // #2: 일괄 완료 후 streak/통계 갱신 (기존 batchComplete는 호출하지 않았음)
          useProfileStore.getState().calculateStreak()
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      batchDelete: async (ids) => {
        const tasksToDelete = get().tasks.filter((t) => ids.includes(t.id))
        if (tasksToDelete.length === 0) return

        // Undo 스냅샷
        const snapshots = tasksToDelete.map((t) => ({ ...t }))
        useUndoStore.getState().pushAction({
          type: 'batch-delete',
          label: `${tasksToDelete.length}개 작업이 삭제되었습니다`,
          undo: async () => {
            for (const snap of snapshots) {
              const newId = await dbAddTask({ ...snap, id: undefined } as Omit<Task, 'id'>)
              const restored: SavedTask = { ...snap, id: newId }
              set((state) => ({ tasks: [...state.tasks, restored] }))
            }
          },
          redo: async () => {
            const syncIds = snapshots.map(s => s.syncId)
            const currentTasks = get().tasks.filter(t => syncIds.includes(t.syncId))
            const currentIds = currentTasks.map(t => t.id)
            if (currentIds.length === 0) return
            await dbBulkDeleteTasks(currentIds)
            for (const t of currentTasks) {
              deleteTaskFromCloud(t.syncId).catch(console.error)
            }
            set((state) => ({
              tasks: state.tasks.filter((t) => !currentIds.includes(t.id)),
              activeTaskId: currentIds.includes(state.activeTaskId ?? -1) ? null : state.activeTaskId,
            }))
          },
        })

        try {
          const tasksToSync = get().tasks.filter(t => ids.includes(t.id))
          await dbBulkDeleteTasks(ids)
          for (const t of tasksToSync) {
            deleteTaskFromCloud(t.syncId).catch(console.error)
            if (t.googleTaskId && isGoogleTasksSyncEnabled()) {
              deleteTaskFromGoogle(t.googleTaskId).catch(console.error)
            }
          }
          set((state) => ({
            tasks: state.tasks.filter((t) => !ids.includes(t.id)),
            activeTaskId: ids.includes(state.activeTaskId ?? -1) ? null : state.activeTaskId,
          }))
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      batchMoveCategory: async (ids, categoryId) => {
        try {
          await dbBulkUpdateTasks(ids, { categoryId })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              ids.includes(t.id) ? { ...t, categoryId, updatedAt: new Date().toISOString() } : t
            ),
          }))
          for (const id of ids) {
            const t = get().tasks.find(task => task.id === id)
            if (t) pushTask(t).catch(console.error)
          }
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      batchSetPriority: async (ids, priority) => {
        try {
          await dbBulkUpdateTasks(ids, { priority })
          set((state) => ({
            tasks: state.tasks.map((t) =>
              ids.includes(t.id) ? { ...t, priority, updatedAt: new Date().toISOString() } : t
            ),
          }))
          for (const id of ids) {
            const t = get().tasks.find(task => task.id === id)
            if (t) pushTask(t).catch(console.error)
          }
        } catch (err) {
          set({ error: (err as Error).message })
        }
      },

      refreshFromDb: async () => {
        const tasks = await getAllTasks() as SavedTask[]
        set({ tasks, isHydrated: true })
      },

      seedTutorialTasks: async () => {
        // 이미 동기화된 작업이 있으면 튜토리얼 생성 건너뛰기
        const existing = get().tasks
        if (existing.some((t) => t.syncId)) return

        const tutorials = [
          { title: 'To-Do List에 오신 것을 환영합니다! 👋', categoryId: null, priority: 'medium' },
          { title: '이 작업을 오른쪽으로 스와이프하여 완료해보세요 👉', categoryId: null, priority: 'high' },
          { title: '작업을 탭하여 상세 내용을 확인하세요 📝', categoryId: null, priority: 'low' },
          { title: '새로운 작업을 추가하려면 아래 + 버튼을 누르세요 ➕', categoryId: null, priority: 'medium' },
        ]

        const now = new Date().toISOString()
        const today = toLocalDateStr(new Date())

        for (const t of tutorials) {
          await get().addTask({
            title: t.title,
            categoryId: t.categoryId,
            priority: t.priority as TaskPriority,
            status: 'pending',
            isFlagged: false,
            isStarred: false,
            dueDate: today,
            alarm: { enabled: false },
            repeat: { type: 'none', interval: 0 },
            subtasks: [],
            createdAt: now,
            updatedAt: now,
            sortOrder: 0,
          })
        }
      },
      }
    },
    { name: 'task-store' }
  )
)

// ─── Selectors ────────────────────────────────────
// M7: Memoize sorted tasks — only re-sort when tasks reference changes
let _cachedTasks: TaskState['tasks'] | null = null
let _cachedSorted: TaskState['tasks'] = []
const selectSortedTasks = (state: TaskState) => {
  if (state.tasks !== _cachedTasks) {
    _cachedTasks = state.tasks
    _cachedSorted = [...state.tasks].sort((a, b) => a.sortOrder - b.sortOrder)
  }
  return _cachedSorted
}

const selectActiveTask = (state: TaskState) =>
  state.tasks.find((t) => t.id === state.activeTaskId) ?? null

export function useSortedTasks() {
  return useTaskStore(selectSortedTasks)
}

export function useActiveTask() {
  return useTaskStore(selectActiveTask)
}

export function useTasksByDate(date: string) {
  return useTaskStore((state) =>
    state.tasks.filter((t) => t.dueDate === date)
  )
}
